/* eslint-disable indent */
import produce, { Draft } from 'immer';
import _get from 'lodash/get';
import { current } from '@reduxjs/toolkit';
import { CONFIG, POSTS, SET_BLOCK_POPULATING } from '../../constants/actionTypes/thirdPartyContent';
import { ISBPost, ISBConfig, ISBConfigResponse, ISBPostRaw, ISBPostContents } from '../../types/thirdPartyContent';

interface State {
    staffbase: {
        config: null | ISBConfig;
        posts: ISBPost[];
        loadingConfig: boolean;
        loadingPosts: boolean;
        isLazyLoading: boolean;
        error: null | string;
        populatingID: string;
    };
}

type Vendor = 'staffbase';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const initialState: State = {
    staffbase: {
        config: null,
        posts: [],
        loadingConfig: false,
        loadingPosts: false,
        isLazyLoading: false,
        error: null,
        populatingID: '',
    },
};

const thirdPartyContent = produce((draft: Draft<State>, action) => {
    const { type, payload }: { type: string; payload: any } = action;
    switch (type) {
        case SET_BLOCK_POPULATING:
            draft[payload.vendor as Vendor].populatingID = action.payload.blockIndex;
            break;
        case CONFIG.FETCH_BEGIN:
            draft[payload.vendor as Vendor].loadingConfig = true;
            break;
        case CONFIG.FETCH_SUCCESS: {
            const { config, colors, slug, name, image }: ISBConfigResponse = payload;
            draft[payload.vendor as Vendor].config = {
                colors,
                languages: config.availableLocales,
                defaultLanguage: config.defaultLocale,
                studioUrl: `${
                    config.whitelabelConfig?.frontendURL
                        ? config.whitelabelConfig?.frontendURL
                        : `https://${slug}.staffbase.com`
                }/admin/plugin/news`,
                appName: name,
                logoUrl: config.backofficeLogoCollapsed?.icon.url || image?.icon.url,
            };
            draft[payload.vendor as Vendor].error = null;
            draft[payload.vendor as Vendor].loadingConfig = false;
            break;
        }
        case CONFIG.FETCH_ERROR:
            draft[payload.vendor as Vendor].error = payload.error;
            draft[payload.vendor as Vendor].loadingConfig = false;
            break;
        case POSTS.FETCH_BEGIN:
            draft[payload.vendor as Vendor].loadingPosts = true;
            draft[payload.vendor as Vendor].isLazyLoading = !!payload?.isLazyLoading;
            break;
        case POSTS.FETCH_SUCCESS: {
            const { offset } = payload;
            const posts = payload.data
                ? payload.data
                      .map(
                          (post: ISBPostRaw): ISBPost => {
                              const author = post.author ? `${post.author.firstName} ${post.author.lastName}` : '';
                              const channels = Object.entries(post.channel.config.localization).reduce<{
                                  [language: string]: string;
                              }>((curr, [language, { title }]) => {
                                  curr[language] = title;
                                  return curr;
                              }, {});
                              const contents = Object.entries(post.contents).reduce<ISBPostContents>(
                                  (curr, [language, localContent]) => {
                                      curr[language] = {
                                          content: localContent.content,
                                          image: _get(localContent, 'image.compact.url', ''),
                                          teaser: localContent.teaser,
                                          title: localContent.title,
                                      };
                                      return curr;
                                  },
                                  {}
                              );
                              const published = new Date(post.published);
                              const updated = new Date(post.updated);
                              return {
                                  id: post.id,
                                  postUrl: _get(post, 'links.detail_view.href'),
                                  author,
                                  channels,
                                  // @ts-ignore Index signature issue, fix later
                                  contents,
                                  published: `${published.getDate()} ${
                                      MONTHS[published.getMonth()]
                                  } ${published.getFullYear()}`,
                                  updated: `${updated.getDate()} ${
                                      MONTHS[updated.getMonth()]
                                  } ${updated.getFullYear()}`,
                              };
                          }
                      )
                      .filter((post: ISBPost) => !post.published.includes('undefined')) // Filtering out unpublished posts until SB API is updated
                : [];

            let uniquePosts: Array<ISBPost> = [];

            /* remove duplicate posts from API result and list of posts in the store
            TODO: remove the extra loop and check for unique posts when posts response 
            from server-side is consistent and not duplicating posts in the front-end */
            uniquePosts = [
                ...new Map(
                    (offset && offset !== 0 ? current(draft[payload.vendor as Vendor].posts) : posts)
                        .concat(posts)
                        .map((post: ISBPost) => [post.id, post])
                ).values(),
            ] as Array<ISBPost>;

            draft[payload.vendor as Vendor].posts = uniquePosts;
            draft[payload.vendor as Vendor].error = null;
            draft[payload.vendor as Vendor].loadingPosts = false;
            draft[payload.vendor as Vendor].isLazyLoading = false;
            break;
        }
        case POSTS.FETCH_ERROR:
            draft[payload.vendor as Vendor].error = payload.error;
            draft[payload.vendor as Vendor].loadingPosts = false;
            draft[payload.vendor as Vendor].isLazyLoading = false;
            break;
        case POSTS.RESET_POSTS:
            draft[payload.vendor as Vendor].posts = [];
            break;
        default:
    }
}, initialState);

export default thirdPartyContent;
